import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Bell, BellOff, Trash2, Zap, Info, AlertCircle } from "lucide-react";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

export default function PushSettingsPage() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [subs, setSubs] = useState([]);
  const [prefs, setPrefs] = useState([]);
  const [vapid, setVapid] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const sup = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(sup);
    if (sup) setPermission(Notification.permission);
    api.get("/push/vapid-public-key").then(r => setVapid(r.data)).catch(() => setVapid(null));
    api.get("/push/subscriptions").then(r => setSubs(r.data)).catch(() => setSubs([]));
    api.get("/push/preferences").then(r => setPrefs(r.data)).catch(() => setPrefs([]));
  }, []);

  const ensureSW = async () => {
    const reg = await navigator.serviceWorker.getRegistration("/sw.js");
    if (reg) return reg;
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  };

  const subscribe = async () => {
    setErr(null); setBusy(true);
    try {
      if (!supported) throw new Error("Il browser non supporta Web Push.");
      if (!vapid?.configured || !vapid?.public_key) {
        throw new Error("Le notifiche Web Push non sono ancora configurate sul server.");
      }
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") throw new Error("Permesso non concesso dal browser.");
      const reg = await ensureSW();
      const existing = await reg.pushManager.getSubscription();
      const sub = existing || await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid.public_key),
      });
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint, keys: json.keys,
        device_label: `${navigator.platform} · ${navigator.userAgent.slice(0,80)}`,
      });
      toast.success("Sottoscrizione registrata su questo dispositivo.");
      const r = await api.get("/push/subscriptions"); setSubs(r.data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex.message || "Errore");
    } finally { setBusy(false); }
  };

  const revoke = async (s) => {
    if (!window.confirm(`Revocare la sottoscrizione "${s.device_label || "questo dispositivo"}"?`)) return;
    try {
      await api.delete(`/push/subscriptions/${s.id}`);
      // Try to unregister local subscription if this is current device
      try {
        const reg = await navigator.serviceWorker.getRegistration("/sw.js");
        const local = reg && await reg.pushManager.getSubscription();
        if (local) await local.unsubscribe();
      } catch {}
      const r = await api.get("/push/subscriptions"); setSubs(r.data);
      toast.success("Sottoscrizione revocata.");
    } catch { /* toast */ }
  };

  const testPush = async () => {
    setErr(null); setBusy(true);
    try {
      const r = await api.post("/push/test");
      const sent = (r.data.results || []).filter(x => x.status === "sent").length;
      const removed = (r.data.results || []).filter(x => x.status === "removed").length;
      const failed = (r.data.results || []).filter(x => x.status === "failed").length;
      toast.success(`Test push inviato: ${sent} riuscite, ${failed} fallite, ${removed} rimosse. Anche la notifica interna è stata creata.`);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const savePrefs = async () => {
    try {
      await api.put("/push/preferences", prefs);
      toast.success("Preferenze salvate.");
    } catch { /* toast */ }
  };

  return (
    <div className="ma-page">
      <div className="ma-page-header">
        <div>
          <h1 className="ma-h1" data-testid="push-settings-title"><Bell size={22} style={{marginRight:8, verticalAlign:"middle"}}/> Notifiche Web Push</h1>
          <div className="ma-sub">Ricevi notifiche browser per eventi agenda, scadenze pratiche, documenti, attività e pagamenti. Il centro notifiche interno resta sempre attivo.</div>
        </div>
      </div>

      <div className="ma-panel">
        <div className="ma-panel-head">Stato dispositivo attuale</div>
        <div className="ma-panel-body">
          <div className="ma-row-kv"><div className="ma-row-k">Supporto browser</div><div className="ma-row-v">{supported ? <span className="ma-pill pill-ok">Sì</span> : <span className="ma-pill pill-err">No</span>}</div></div>
          <div className="ma-row-kv"><div className="ma-row-k">Permesso notifiche</div><div className="ma-row-v">{permission}</div></div>
          <div className="ma-row-kv"><div className="ma-row-k">VAPID configurato</div><div className="ma-row-v">{vapid?.configured ? <span className="ma-pill pill-ok">Sì</span> : <span className="ma-pill pill-warn">No</span>}</div></div>
          {isIOS && (
            <div className="ma-warning">
              <Info size={14}/>
              <span>Su iOS le notifiche Web Push funzionano solo per l'app installata come PWA (Home screen) con iOS ≥ 16.4. Aggiungi l'app alla schermata Home per abilitarle.</span>
            </div>
          )}
          <div className="ma-form-actions" style={{marginTop:12}}>
            <button className="ma-btn-primary-sm" onClick={subscribe} disabled={busy || !supported || !vapid?.configured || !vapid?.public_key} data-testid="btn-push-subscribe">
              <Bell size={14}/> Attiva su questo dispositivo
            </button>
            <button className="ma-btn-secondary-sm" onClick={testPush} disabled={busy || subs.filter(s=>s.is_active).length===0} data-testid="btn-push-test">
              <Zap size={14}/> Invia notifica di prova
            </button>
          </div>
          {err && <div className="ma-error"><AlertCircle size={14}/> {typeof err==="string"?err:"Errore"}</div>}
        </div>
      </div>

      <div className="ma-panel">
        <div className="ma-panel-head">Sottoscrizioni registrate ({subs.filter(s=>s.is_active).length} attive)</div>
        <div className="ma-panel-body">
          {subs.length === 0 && <div className="ma-empty">Nessuna sottoscrizione. Attivala su questo dispositivo o su altri browser/PC.</div>}
          {subs.map(s => (
            <div key={s.id} className="ma-sub-row" data-testid={`push-sub-${s.id}`}>
              <div>
                <div className="ma-doc-name">{s.device_label || "Dispositivo"}</div>
                <div className="ma-sub-xs">Registrato: {new Date(s.created_at).toLocaleString("it-IT")}
                  {s.last_used_at && <> · Ultimo uso: {new Date(s.last_used_at).toLocaleString("it-IT")}</>}
                  {!s.is_active && <> · <span className="ma-pill pill-err">Revocata {s.revoke_reason ? `(${s.revoke_reason})` : ""}</span></>}
                </div>
              </div>
              {s.is_active && (
                <button className="ma-btn-link ma-danger" onClick={()=>revoke(s)} data-testid={`btn-revoke-${s.id}`}>
                  <Trash2 size={14}/> Revoca
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="ma-panel">
        <div className="ma-panel-head">Preferenze per categoria</div>
        <div className="ma-panel-body">
          {prefs.map((p, idx) => (
            <div key={p.category} className="ma-pref-row">
              <label className="ma-checkbox">
                <input type="checkbox" checked={p.enabled}
                        onChange={e => { const c=[...prefs]; c[idx]={...p, enabled:e.target.checked}; setPrefs(c); }}
                        data-testid={`pref-enabled-${p.category}`}/>
                <span>{p.label}</span>
              </label>
              <div>
                <label className="ma-label">Anticipo (minuti)</label>
                <input type="number" min="0" max="43200" className="ma-input-sm"
                        value={p.advance_minutes}
                        onChange={e => { const c=[...prefs]; c[idx]={...p, advance_minutes: parseInt(e.target.value||"0")}; setPrefs(c); }}
                        data-testid={`pref-advance-${p.category}`}/>
              </div>
            </div>
          ))}
          <div className="ma-form-actions">
            <button className="ma-btn-primary" onClick={savePrefs} data-testid="btn-save-prefs">Salva preferenze</button>
          </div>
        </div>
      </div>
    </div>
  );
}
